import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Loader2, Search, Paperclip, X as XIcon,
  CheckCircle, Clock, ChevronRight, RefreshCw, Send, XCircle,
  Inbox, MessageCircle, MessageSquare, User, Filter
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Attachment limits (mirror tenant-side support)
const MAX_ATTACHMENTS = 5;
const TARGET_KB = 300;
const HARD_CAP_KB = 600;
const ALLOWED_MIME = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

async function compressForSupport(file) {
  const passes = [
    { maxSizeMB: TARGET_KB / 1024, maxWidthOrHeight: 1600, initialQuality: 0.85 },
    { maxSizeMB: HARD_CAP_KB / 1024, maxWidthOrHeight: 1280, initialQuality: 0.78 },
  ];
  let compressed = file;
  for (const p of passes) {
    compressed = await imageCompression(file, {
      ...p,
      useWebWorker: false,
      fileType: file.type === 'image/webp' ? 'image/webp' : 'image/jpeg',
    });
    if (compressed.size / 1024 <= HARD_CAP_KB) break;
  }
  if (compressed.size / 1024 > HARD_CAP_KB) {
    throw new Error(`Could not compress below ${HARD_CAP_KB} KB without making text unreadable.`);
  }
  return compressed;
}

// Admin API instance
const adminApi = axios.create({
  baseURL: API_URL,
});

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * <img> wrapper that fetches the attachment via the authenticated `adminApi`
 * client (so the admin JWT goes in the Authorization header) and renders it
 * from an object URL.
 */
function AuthedAdminImage({ src, alt, className }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let url = null;
    adminApi
      .get(src, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setBlobUrl(null);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [src]);
  if (!blobUrl) {
    return <div className={`${className} animate-pulse bg-black/10`} />;
  }
  return <img src={blobUrl} alt={alt} className={className} loading="lazy" />;
}

export const AdminSupport = () => {
  const navigate = useNavigate();
  const { adminRole } = useOutletContext();
  const isPrimaryAdmin = adminRole === 'primary';
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ open: 0, responded: 0, closed: 0, total: 0 });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [tenants, setTenants] = useState([]);
  
  // Reply state
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Attachment state (mirrors tenant SupportBubble)
  const [attachments, setAttachments] = useState([]);
  const [compressing, setCompressing] = useState(false);
  const fileInputRef = useRef(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (tenantFilter && tenantFilter !== 'all') params.tenant_id = tenantFilter;
      if (searchQuery) params.q = searchQuery;
      
      const [ticketsRes, statsRes, tenantsRes] = await Promise.all([
        adminApi.get('/api/admin/support/tickets', { params }),
        adminApi.get('/api/admin/support/stats'),
        adminApi.get('/api/admin/support/tenants'),
      ]);
      
      setTickets(ticketsRes.data);
      setStats(statsRes.data);
      setTenants(tenantsRes.data);
    } catch (err) {
      console.error('Failed to fetch support data:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, tenantFilter, searchQuery, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (selectedTicket?.messages) {
      scrollToBottom();
    }
  }, [selectedTicket?.messages]);

  const handleSelectTicket = async (ticket) => {
    try {
      const res = await adminApi.get(`/api/admin/support/tickets/${ticket.id}`);
      setSelectedTicket(res.data);
    } catch (err) {
      toast.error('Failed to load ticket');
    }
  };

  // ── Attachments (admin reply) ──
  const handleSelectFiles = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);

    setCompressing(true);
    try {
      for (const file of files) {
        if (!ALLOWED_MIME.includes(file.type.toLowerCase())) {
          toast.error(`'${file.name}' is not a supported image (PNG, JPG, WEBP only)`);
          continue;
        }

        const tmpId = `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const originalSizeKb = Math.round(file.size / 1024);

        let compressed;
        try {
          compressed = await compressForSupport(file);
        } catch (e) {
          toast.error(e.message || 'Failed to compress image');
          continue;
        }
        const compressedSizeKb = Math.round(compressed.size / 1024);
        const preview = URL.createObjectURL(compressed);

        let added = false;
        setAttachments((prev) => {
          if (prev.length >= MAX_ATTACHMENTS) return prev;
          added = true;
          return [
            ...prev,
            {
              id: tmpId,
              file: compressed,
              originalName: file.name,
              preview,
              originalSizeKb,
              compressedSizeKb,
              status: 'uploading',
            },
          ];
        });
        if (!added) {
          URL.revokeObjectURL(preview);
          toast.error(`Maximum ${MAX_ATTACHMENTS} screenshots per reply`);
          break;
        }

        try {
          const fd = new FormData();
          fd.append('file', compressed);
          const res = await adminApi.post('/api/admin/support/upload-image', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          setAttachments((prev) =>
            prev.map((a) => (a.id === tmpId ? { ...a, status: 'uploaded', path: res.data.path } : a)),
          );
        } catch (e) {
          const detail = e.response?.data?.detail || 'Upload failed';
          setAttachments((prev) =>
            prev.map((a) => (a.id === tmpId ? { ...a, status: 'error', error: String(detail) } : a)),
          );
          toast.error(String(detail));
        }
      }
    } finally {
      setCompressing(false);
    }
  }, []);

  const removeAttachment = (id) => {
    setAttachments((prev) => {
      const a = prev.find((x) => x.id === id);
      if (a?.preview) URL.revokeObjectURL(a.preview);
      return prev.filter((x) => x.id !== id);
    });
  };

  const clearAttachments = useCallback(() => {
    setAttachments((prev) => {
      prev.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
      return [];
    });
  }, []);

  useEffect(() => () => clearAttachments(), [clearAttachments]);

  const uploadedPaths = () => attachments.filter((a) => a.status === 'uploaded' && a.path).map((a) => a.path);
  const hasUploadingAttachment = () => attachments.some((a) => a.status === 'uploading');

  const handlePaste = useCallback(
    (e) => {
      const items = e.clipboardData?.items;
      if (!items || items.length === 0) return;
      const files = [];
      for (const item of items) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const f = item.getAsFile();
          if (f) {
            const named = new File([f], f.name && f.name !== 'image.png' ? f.name : `pasted-${Date.now()}.png`, {
              type: f.type || 'image/png',
            });
            files.push(named);
          }
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        handleSelectFiles(files);
      }
    },
    [handleSelectFiles],
  );

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim() || !selectedTicket) return;
    if (hasUploadingAttachment()) {
      toast.error('Please wait for screenshots to finish uploading');
      return;
    }

    setSending(true);
    try {
      await adminApi.post(`/api/admin/support/tickets/${selectedTicket.id}/messages`, {
        message: replyMessage.trim(),
        attachment_paths: uploadedPaths(),
      });
      
      // Refresh ticket
      const res = await adminApi.get(`/api/admin/support/tickets/${selectedTicket.id}`);
      setSelectedTicket(res.data);
      setReplyMessage('');
      clearAttachments();
      toast.success('Reply sent');
      
      // Refresh list
      fetchData();
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      await adminApi.patch(`/api/admin/support/tickets/${ticketId}`, {
        status: newStatus
      });
      
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
      
      toast.success(`Ticket ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update ticket');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <Badge className="bg-amber-100 text-amber-800 border-amber-300">Open</Badge>;
      case 'responded':
        return <Badge className="bg-green-100 text-green-800 border-green-300">Responded</Badge>;
      case 'closed':
        return <Badge className="bg-gray-100 text-gray-600 border-gray-300">Closed</Badge>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Open</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.open}</p>
                </div>
                <Inbox className="w-8 h-8 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Responded</p>
                  <p className="text-2xl font-bold text-green-600">{stats.responded}</p>
                </div>
                <MessageCircle className="w-8 h-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Closed</p>
                  <p className="text-2xl font-bold text-gray-600">{stats.closed}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-gray-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
                </div>
                <MessageSquare className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="responded">Responded</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>

              <Select value={tenantFilter} onValueChange={setTenantFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Tenant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tenants</SelectItem>
                  {tenants.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search subject..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>

              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Ticket List */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Tickets ({tickets.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {tickets.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Inbox className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No tickets found</p>
                </div>
              ) : (
                <div className="divide-y max-h-[600px] overflow-y-auto">
                  {tickets.map((ticket) => (
                    <button
                      key={ticket.id}
                      onClick={() => handleSelectTicket(ticket)}
                      className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                        selectedTicket?.id === ticket.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                      }`}
                      data-testid={`admin-ticket-${ticket.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 mr-3">
                          <p className="font-medium text-sm truncate">
                            {ticket.subject || 'No subject'}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {ticket.tenant_name} • {ticket.created_by_name}
                          </p>
                        </div>
                        {getStatusBadge(ticket.status)}
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDistanceToNow(new Date(ticket.updated_at), { addSuffix: true })}
                        </span>
                        <span>{ticket.message_count} msg{ticket.message_count !== 1 ? 's' : ''}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Ticket Detail */}
          <Card>
            <CardHeader className="pb-3 border-b">
              {selectedTicket ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <CardTitle className="text-lg">{selectedTicket.subject || 'No subject'}</CardTitle>
                    {getStatusBadge(selectedTicket.status)}
                  </div>
                  <div className="text-sm text-gray-500 space-y-1">
                    <p className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {selectedTicket.created_by_name}
                    </p>
                    <p className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {format(new Date(selectedTicket.created_at), 'PPp')}
                    </p>
                  </div>
                </div>
              ) : (
                <CardTitle className="text-lg text-gray-400">Select a ticket</CardTitle>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {selectedTicket ? (
                <div className="flex flex-col h-[500px]">
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {selectedTicket.messages?.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.author_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-lg px-3 py-2 ${
                            msg.author_type === 'admin'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                          {msg.attachments && msg.attachments.length > 0 && (
                            <div className="mt-2 grid grid-cols-2 gap-1.5" data-testid={`admin-msg-attachments-${msg.id}`}>
                              {msg.attachments.map((att) => (
                                <button
                                  key={att.id}
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      const res = await adminApi.get(att.url, { responseType: 'blob' });
                                      const url = URL.createObjectURL(res.data);
                                      setLightboxUrl(url);
                                    } catch (e) {
                                      toast.error('Could not open attachment');
                                    }
                                  }}
                                  className="block rounded overflow-hidden border border-white/20 hover:opacity-90 text-left"
                                  title={`${att.file_name} (${Math.round(att.size_bytes/1024)} KB)`}
                                  data-testid={`admin-msg-attachment-${att.id}`}
                                >
                                  <AuthedAdminImage src={att.url} alt={att.file_name} className="w-full h-24 object-cover bg-black/10" />
                                </button>
                              ))}
                            </div>
                          )}
                          <p className={`text-[10px] mt-1 ${msg.author_type === 'admin' ? 'text-blue-200' : 'text-gray-500'}`}>
                            {msg.author_name} • {format(new Date(msg.created_at), 'p')}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Actions */}
                  {selectedTicket.status !== 'closed' ? (
                    isPrimaryAdmin ? (
                    <div className="border-t p-3">
                      <form onSubmit={handleSendReply} className="space-y-3">
                        <Textarea
                          value={replyMessage}
                          onChange={(e) => setReplyMessage(e.target.value)}
                          onPaste={handlePaste}
                          placeholder="Type your reply... (paste screenshots with Ctrl+V)"
                          className="min-h-[80px] resize-none"
                          data-testid="admin-reply-input"
                        />

                        {/* Attachment strip */}
                        <div className="space-y-2">
                          {attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2" data-testid="admin-support-attachments-strip">
                              {attachments.map((a) => (
                                <div
                                  key={a.id}
                                  className="relative group rounded-md border border-gray-200 bg-white overflow-hidden w-20 h-20"
                                  data-testid={`admin-support-attachment-${a.id}`}
                                >
                                  <img src={a.preview} alt={a.originalName} className="w-full h-full object-cover" />
                                  {a.status === 'uploading' && (
                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                      <Loader2 className="w-4 h-4 text-white animate-spin" />
                                    </div>
                                  )}
                                  {a.status === 'error' && (
                                    <div className="absolute inset-0 bg-red-600/70 flex items-center justify-center text-[10px] text-white text-center px-1">
                                      failed
                                    </div>
                                  )}
                                  {a.status === 'uploaded' && (
                                    <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate">
                                      {a.compressedSizeKb} KB
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => removeAttachment(a.id)}
                                    className="absolute -top-1 -right-1 bg-gray-800 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center shadow"
                                    title="Remove"
                                    data-testid={`admin-support-attachment-remove-${a.id}`}
                                  >
                                    <XIcon className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <button
                            type="button"
                            className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={sending || compressing || attachments.length >= MAX_ATTACHMENTS}
                            data-testid="admin-support-attach-btn"
                          >
                            <Paperclip className="w-4 h-4" />
                            {compressing ? 'Compressing…' : 'Attach screenshot'}
                            {attachments.length > 0 && ` (${attachments.length}/${MAX_ATTACHMENTS})`}
                          </button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept={ALLOWED_MIME.join(',')}
                            multiple
                            className="hidden"
                            onChange={(e) => {
                              handleSelectFiles(e.target.files);
                              e.target.value = '';
                            }}
                            data-testid="admin-support-attach-input"
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleUpdateStatus(selectedTicket.id, 'closed')}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Close Ticket
                          </Button>
                          <Button
                            type="submit"
                            disabled={!replyMessage.trim() || sending || hasUploadingAttachment()}
                            data-testid="admin-send-reply"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            {sending ? 'Sending...' : 'Send Reply'}
                          </Button>
                        </div>
                      </form>
                    </div>
                    ) : (
                    <div className="border-t p-3 bg-gray-50 text-center">
                      <p className="text-sm text-gray-500">View-only access — replies require primary admin</p>
                    </div>
                    )
                  ) : (
                    <div className="border-t p-3 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500">
                          <CheckCircle className="w-4 h-4 inline mr-1" />
                          This ticket is closed
                        </p>
                        {isPrimaryAdmin && <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUpdateStatus(selectedTicket.id, 'open')}
                        >
                          Reopen
                        </Button>}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-[500px] flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3" />
                    <p>Select a ticket to view details</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Lightbox modal */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
          onClick={() => {
            URL.revokeObjectURL(lightboxUrl);
            setLightboxUrl(null);
          }}
          data-testid="admin-support-lightbox"
        >
          <button
            type="button"
            className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2"
            onClick={(e) => {
              e.stopPropagation();
              URL.revokeObjectURL(lightboxUrl);
              setLightboxUrl(null);
            }}
            data-testid="admin-support-lightbox-close"
          >
            <XIcon className="w-6 h-6" />
          </button>
          <img
            src={lightboxUrl}
            alt="Attachment preview"
            className="max-w-full max-h-full object-contain rounded shadow-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default AdminSupport;
